import { loadScript } from "@/utils/helpers";
import type {
  GooglePayGlobalOptions,
  GooglePayMerchantInfo,
  GooglePayMerchantConfig,
  GooglePayMerchantInfoJwt,
  GooglePayMerchantInfoJwtResponse,
  GetGooglePayMerchantInfoParams,
  GooglePayTransactionInfo,
  GooglePayPaymentDataRequest,
  GooglePayPaymentData,
  GooglePayIsReadyToPayRequest,
  CreateGooglePayButtonParams,
  GooglePayInstance,
  GooglePayButtonOptions,
} from "./googlepay";

/**
 * Build merchantInfo from API config, optionally enriched with the merchant info
 * returned by Nuvei's /googlePayMerchantInfoJwt endpoint. When present, the JWT
 * response provides the Google merchantId/merchantName plus the merchantOrigin and
 * authJwt required for Nuvei's domain-verification (JWT) solution.
 */
export const buildMerchantInfoFromConfig = (
  config: GooglePayMerchantConfig,
  merchantInfoJwt?: GooglePayMerchantInfoJwt
): GooglePayMerchantInfo => ({
  merchantId: merchantInfoJwt?.merchantId ?? config.merchant_id,
  merchantName: merchantInfoJwt?.merchantName ?? config.merchantName ?? "",
  ...(merchantInfoJwt?.merchantOrigin != null && {
    merchantOrigin: merchantInfoJwt.merchantOrigin,
  }),
  ...(merchantInfoJwt?.authJwt != null && {
    authJwt: merchantInfoJwt.authJwt,
  }),
});

export const loadGooglePaySDK = async (): Promise<void> => {
  await loadScript("https://pay.google.com/gp/p/js/pay.js");
};

export const initGooglePaySDKInstance = async (
  options: GooglePayGlobalOptions
): Promise<GooglePayInstance> => {
  await loadGooglePaySDK();
  const api = window.google?.payments?.api;
  const PaymentsClient = api?.PaymentsClient;
  if (!PaymentsClient) {
    throw new Error("Google Pay API not found");
  }
  return new PaymentsClient(options);
};

export const getGooglePaySDKInstance = (): GooglePayInstance | undefined => {
  if (!window.google?.payments?.api) {
    throw new Error("Google Pay API not found");
  }
  return window.googlePayInstance;
};

const baseRequest = {
  apiVersion: 2,
  apiVersionMinor: 0,
};

const allowedCardNetworks = ["MASTERCARD", "VISA"];
const allowedCardAuthMethods = ["PAN_ONLY", "CRYPTOGRAM_3DS"];

/** Time-box the Nuvei merchant-info request so a hung endpoint can't block checkout. */
const googlePayMerchantInfoTimeoutMs = 10_000;

const getTokenizationSpecification = () => ({
  type: "PAYMENT_GATEWAY",
  parameters: {
    gateway: import.meta.env.VITE_GOOGLE_PAY_GATEWAY as string,
    gatewayMerchantId: import.meta.env
      .VITE_GOOGLE_PAY_GATEWAY_MERCHANT_ID as string,
  },
});

const baseCardPaymentMethod = {
  type: "CARD",
  parameters: {
    allowedAuthMethods: allowedCardAuthMethods,
    allowedCardNetworks: allowedCardNetworks,
  },
};

const getCardPaymentMethod = () => ({
  ...baseCardPaymentMethod,
  tokenizationSpecification: getTokenizationSpecification(),
});

export const getGoogleIsReadyToPayRequest =
  (): GooglePayIsReadyToPayRequest => ({
    ...baseRequest,
    allowedPaymentMethods: [getCardPaymentMethod()],
  });

export const getGooglePaymentDataRequest = (
  merchantInfo: GooglePayMerchantInfo,
  transactionInfo: GooglePayTransactionInfo
): GooglePayPaymentDataRequest => {
  return {
    ...baseRequest,
    allowedPaymentMethods: [getCardPaymentMethod()],
    transactionInfo,
    merchantInfo,
  };
};

/**
 * Fetch the Google Pay merchant info + JWT from Nuvei for the current session.
 *
 * Nuvei's domain-verification (JWT) solution registers our domains once (done by
 * the backend on startup) and then, per transaction, the merchant must add the
 * returned merchantOrigin + authJwt to the Google Pay PaymentDataRequest. This
 * calls /googlePayMerchantInfoJwt directly — it needs only the session token and
 * origin, no merchant secret/checksum — and returns the merchantInfo to attach.
 *
 * @throws if the base URL is not configured or Nuvei does not return SUCCESS.
 */
export const getGooglePayMerchantInfoJwt = async ({
  sessionToken,
  merchantOrigin,
}: GetGooglePayMerchantInfoParams): Promise<GooglePayMerchantInfoJwt> => {
  const baseUrl = import.meta.env.VITE_NUVEI_API_BASE_URL as string;
  if (!baseUrl) {
    throw new Error(
      "Nuvei API base URL (VITE_NUVEI_API_BASE_URL) is not configured"
    );
  }

  const url = new URL("/ppp/api/v1/googlePayMerchantInfoJwt.do", baseUrl);
  url.searchParams.set("merchantOrigin", merchantOrigin);
  url.searchParams.set("sessionToken", sessionToken);

  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    googlePayMerchantInfoTimeoutMs
  );

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: controller.signal,
      // The session token travels in the query string (Nuvei's required shape), so keep this
      // request out of any cache and strip the referrer to reduce where the token can leak.
      cache: "no-store",
      referrerPolicy: "no-referrer",
    });
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw new Error(
      `Google Pay merchant info request failed with status ${response.status}`
    );
  }

  let data: GooglePayMerchantInfoJwtResponse;
  try {
    data = (await response.json()) as GooglePayMerchantInfoJwtResponse;
  } catch {
    throw new Error("Google Pay merchant info response was not valid JSON");
  }

  // authJwt + merchantOrigin are required for the domain-verification request;
  // an empty authJwt is treated as a failure (Nuvei should never return one).
  if (data.status !== "SUCCESS" || !data.merchantInfo?.authJwt) {
    throw new Error(
      `Google Pay merchant info request returned status "${data.status}"`
    );
  }

  return data.merchantInfo;
};

/**
 * Check if Google Pay is available.
 * @returns Promise resolving to true if ready, false if instance missing or not available.
 */
export const isGooglePayAvailable = async (): Promise<boolean> => {
  const instance = getGooglePaySDKInstance();
  if (!instance) {
    return false;
  }
  const response = await instance.isReadyToPay(getGoogleIsReadyToPayRequest());
  return response.result;
};

/**
 * Request Google Pay payment data (shows the payment sheet) without creating a button.
 * Use when you need to get merchant config from start-transaction first (e.g. with user-entered amount).
 * @returns Promise resolving with payment data when user completes the sheet.
 */
export const requestGooglePayPaymentData = async (
  merchantConfig: GooglePayMerchantConfig,
  transactionInfo: GooglePayTransactionInfo,
  merchantInfoJwt?: GooglePayMerchantInfoJwt
): Promise<GooglePayPaymentData> => {
  const instance = getGooglePaySDKInstance();
  if (!instance) {
    throw new Error("Google Pay API not found");
  }
  const merchantInfo = buildMerchantInfoFromConfig(
    merchantConfig,
    merchantInfoJwt
  );
  const request = getGooglePaymentDataRequest(merchantInfo, transactionInfo);
  return instance.loadPaymentData(request);
};

/**
 * Create a Google Pay-styled button that calls the given handler on click.
 * Use when you need to run start-transaction first (e.g. with user-entered amount) before showing the payment sheet.
 * @returns Promise resolving with the button HTMLElement, or null if not available.
 */
export const createGooglePayDeferredButton = async (
  onClick: () => void | Promise<void>,
  locale?: string
): Promise<HTMLElement | null> => {
  const isReady = await isGooglePayAvailable();
  if (!isReady) {
    return null;
  }

  const instance = getGooglePaySDKInstance();
  if (!instance) {
    return null;
  }

  const buttonOptions: GooglePayButtonOptions = {
    buttonColor: "default",
    buttonType: "plain",
    buttonRadius: 8,
    buttonBorderType: "default_border",
    buttonSizeMode: "fill",
    ...(locale != null && { buttonLocale: locale }),
    onClick: () => {
      Promise.resolve(onClick()).catch(() => {});
    },
  };

  return instance.createButton(buttonOptions);
};

/**
 * Create the Google Pay button once Google Pay is ready.
 * Waits for availability, then creates a button that uses the given transaction data
 * and calls onPaymentData when the user completes payment.
 * @returns Promise resolving to the button HTMLElement, or null if not available.
 */
export const createGooglePayButton = async (
  params: CreateGooglePayButtonParams
): Promise<HTMLElement | null> => {
  const isReady = await isGooglePayAvailable();
  if (!isReady) {
    return null;
  }

  const instance = getGooglePaySDKInstance();
  if (!instance) {
    return null;
  }

  const {
    merchantInfo,
    merchantConfig,
    transactionInfo,
    onPaymentData,
    onError,
    locale,
    buttonOptions: customButtonOptions,
  } = params;

  if (merchantConfig == null && merchantInfo == null) {
    return null;
  }

  const resolvedMerchantInfo: GooglePayMerchantInfo =
    merchantConfig != null
      ? buildMerchantInfoFromConfig(merchantConfig)
      : merchantInfo!;

  const paymentDataRequest = getGooglePaymentDataRequest(
    resolvedMerchantInfo,
    transactionInfo
  );

  const buttonOptions: GooglePayButtonOptions = {
    buttonColor: "default",
    buttonType: "plain",
    buttonRadius: 8,
    buttonBorderType: "default_border",
    buttonSizeMode: "fill",
    ...(locale != null && { buttonLocale: locale }),
    ...customButtonOptions,
    onClick: () => {
      instance
        .loadPaymentData(paymentDataRequest)
        .then((paymentData) => {
          onPaymentData(paymentData);
        })
        .catch((error) => {
          onError?.(error);
        });
    },
  };

  return instance.createButton(buttonOptions);
};
